import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HOUR_PX, SNAP_MIN, DAY_MS, CATS, startOfDay, sameDay, layout, hhmm, hourLabel } from "./constants";

export function TimedGrid({ days, events, view, onAddSlot, onOpenBlock, onSelectEvent, onPickDay }) {
  const { t, i18n } = useTranslation();
  const LOC = i18n.language || "en";
  const scrollRef = useRef(null);
  const dragRef = useRef(null);
  const [drag, setDrag] = useState(null);

  // Scroll to ~7:00 on mount and whenever the timed view changes
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 7 * HOUR_PX; }, [view]);

  const yToMinutes = (y) => Math.max(0, Math.min(24 * 60, Math.round((y / HOUR_PX * 60) / SNAP_MIN) * SNAP_MIN));

  const onColMouseDown = (e, dayIdx) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y0 = e.clientY - rect.top;
    dragRef.current = { dayIdx, y0, moved: false };
    setDrag({ dayIdx, y0, y1: y0 });
    const onMove = (ev) => {
      const y1 = Math.max(0, Math.min(24 * HOUR_PX, ev.clientY - rect.top));
      if (Math.abs(y1 - y0) > 4) dragRef.current.moved = true;
      setDrag({ dayIdx, y0, y1 });
    };
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const d = dragRef.current; dragRef.current = null;
      setDrag(null);
      if (!d) return;
      const y1 = Math.max(0, Math.min(24 * HOUR_PX, ev.clientY - rect.top));
      const day = days[dayIdx];
      if (!d.moved) {
        onAddSlot(day, Math.floor(yToMinutes(y0) / 60));
        return;
      }
      let a = yToMinutes(Math.min(y0, y1)), b = yToMinutes(Math.max(y0, y1));
      if (b - a < SNAP_MIN) b = a + SNAP_MIN;
      const start = new Date(day); start.setHours(0, a, 0, 0);
      const end = new Date(day); end.setHours(0, b, 0, 0);
      onOpenBlock({ start: start.toISOString(), end: end.toISOString(), category: "leave", title: "" });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const clamp = (ev, day) => {
    const dayStart = startOfDay(day).getTime();
    const dayEnd = dayStart + DAY_MS;
    const segStart = Math.max(ev.start.getTime(), dayStart);
    const segEnd = Math.min(ev.end.getTime(), dayEnd);
    if (segEnd <= segStart) return null;
    return { ...ev, top: (segStart - dayStart) / 3600000 * HOUR_PX, bottom: (segEnd - dayStart) / 3600000 * HOUR_PX };
  };

  const gridCols = { gridTemplateColumns: `repeat(${days.length}, minmax(0,1fr))`, minWidth: days.length > 1 ? 640 : undefined };

  return (
    <>
      {/* headers */}
      <div className="grid grid-cols-[56px_1fr] border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="border-r border-slate-200" />
        <div className="grid" style={gridCols}>
          {days.map((day) => {
            const isToday = sameDay(day, new Date());
            return (
              <button key={day.toISOString()} onClick={() => onPickDay(day)} className="py-2 text-center border-r border-slate-100 last:border-r-0 hover:bg-slate-50 transition-colors">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">{day.toLocaleDateString(LOC, { weekday: "short" })}</div>
                <div className={`text-sm font-semibold mt-0.5 mx-auto w-7 h-7 grid place-items-center rounded-full ${isToday ? "bg-indigo-600 text-white" : "text-slate-800"}`}>{day.getDate()}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* grid */}
      <div ref={scrollRef} className="grid grid-cols-[56px_1fr] overflow-y-auto relative bg-slate-50/30" style={{ maxHeight: "calc(100vh - 20rem)", minHeight: 480 }}>
        {/* time gutter */}
        <div className="border-r border-slate-200 bg-white sticky left-0 z-10">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="text-[10px] text-slate-400 text-right pr-2 -translate-y-1.5" style={{ height: HOUR_PX }}>{h > 0 ? hourLabel(h, LOC) : ""}</div>
          ))}
        </div>
        {/* day columns */}
        <div className="grid" style={gridCols}>
          {days.map((day, dayIdx) => {
            const isToday = sameDay(day, new Date());
            const dayEvents = events.map((ev) => clamp(ev, day)).filter(Boolean);
            const laid = layout(dayEvents);
            const nowMin = (Date.now() - startOfDay(new Date()).getTime()) / 60000;
            return (
              <div
                key={day.toISOString()}
                data-testid={`day-col-${dayIdx}`}
                onMouseDown={(e) => onColMouseDown(e, dayIdx)}
                className="relative border-r border-slate-100 last:border-r-0 cursor-pointer select-none"
                style={{ height: 24 * HOUR_PX }}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="border-b border-slate-100 hover:bg-indigo-50/40 transition-colors" style={{ height: HOUR_PX }} />
                ))}
                {isToday && nowMin > 0 && nowMin < 1440 && (
                  <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: nowMin / 60 * HOUR_PX }}>
                    <div className="relative border-t-2 border-red-500">
                      <span className="absolute -left-1 -top-[5px] w-2.5 h-2.5 rounded-full bg-red-500" />
                    </div>
                  </div>
                )}
                {drag && drag.dayIdx === dayIdx && (
                  <div className="absolute left-1 right-1 z-30 rounded-md bg-indigo-500/20 border border-indigo-400 pointer-events-none"
                    style={{ top: Math.min(drag.y0, drag.y1), height: Math.abs(drag.y1 - drag.y0) }} />
                )}
                {laid.map((ev) => {
                  const cat = CATS[ev.category];
                  const w = 100 / (ev._cols || 1);
                  return (
                    <button
                      key={ev.id}
                      data-testid={`event-${ev.id}`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); onSelectEvent(ev); }}
                      aria-label={`${t(cat.labelKey)} ${hhmm(ev.start, LOC)}`}
                      className={`absolute z-10 rounded-md px-1.5 py-1 text-left overflow-hidden text-[11px] leading-tight focus-visible:ring-2 focus-visible:ring-indigo-500 focus:outline-none ${cat.block}`}
                      style={{ top: ev.top + 1, height: Math.max(ev.bottom - ev.top - 2, 18), left: `calc(${ev._col * w}% + 2px)`, width: `calc(${w}% - 4px)` }}
                    >
                      <span className="font-semibold block truncate">{ev.title}</span>
                      {(ev.bottom - ev.top) > 28 && <span className="block truncate opacity-80">{hhmm(ev.start, LOC)}{ev.subtitle ? ` · ${ev.subtitle}` : ""}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
